import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";
import { useToast } from "@/hooks/use-toast";

export interface ModuleSetting {
  id: string;
  admin_bubble_id: string;
  module_name: string;
  is_enabled: boolean;
  settings: Record<string, any>;
}

export const useModuleSettings = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user's admin_bubble_id to query module settings
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // @ts-ignore
      const { data } = await supabase
        .from("profiles")
        .select("id, role, admin_bubble_id")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch module settings from admin_module_settings table
  const { data: moduleSettings = [], isLoading } = useQuery({
    queryKey: ["module-settings", profile?.admin_bubble_id],
    queryFn: async () => {
      // @ts-ignore
      if (!profile?.admin_bubble_id) return [];
      
      // @ts-ignore
      const { data, error } = await supabase
        // @ts-ignore
        .from("admin_module_settings")
        .select("*")
        // @ts-ignore
        .eq("admin_id", profile.admin_bubble_id)
        .order("module_name");

      if (error) throw error;
      return data || [];
    },
    // @ts-ignore
    enabled: !!profile?.admin_bubble_id,
  });

  // Check if specific module is enabled
  const isModuleEnabled = (moduleName: string): boolean => {
    // @ts-ignore
    const module = moduleSettings.find((m: any) => m.module_name === moduleName);
    // @ts-ignore
    return module?.is_enabled || false;
  };

  // Toggle module mutation
  const toggleModuleMutation = useMutation({
    mutationFn: async ({ moduleName, isEnabled }: { moduleName: string; isEnabled: boolean }) => {
      // @ts-ignore
      if (!profile?.admin_bubble_id) throw new Error("No admin ID");

      // @ts-ignore
      const { error } = await supabase
        // @ts-ignore
        .from("admin_module_settings")
        .upsert({
          // @ts-ignore
          admin_id: profile.admin_bubble_id,
          module_name: moduleName,
          is_enabled: isEnabled,
        }, {
          onConflict: 'admin_id,module_name'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["module-settings"] });
      toast({
        title: "Success",
        description: "Module settings updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    moduleSettings,
    isLoading,
    isModuleEnabled,
    toggleModule: toggleModuleMutation.mutate,
    isTogglingModule: toggleModuleMutation.isPending,
  };
};
